import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import { Html5Qrcode } from "html5-qrcode";

const QRScanner = forwardRef(({ onScan }, ref) => {
  const html5QrcodeRef = useRef(null);
  const audioCtxRef = useRef(null);     // WebAudio context
  const fileBeepRef = useRef(null);     // <audio> fallback
  const scannerId = "reader";

    // ---- one-time audio unlock (required by iOS/Android autoplay policies)
  useEffect(() => {
    fileBeepRef.current = new Audio("/beep.mp3");
    fileBeepRef.current.preload = "auto";

    const unlock = async () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          audioCtxRef.current = audioCtxRef.current || new Ctx();
          await audioCtxRef.current.resume();
        }
      } catch {}
      // “prime” the HTMLAudio element too
      try {
        const a = fileBeepRef.current;
        a.muted = true;
        await a.play();
        a.pause();
        a.currentTime = 0;
        a.muted = false;
      } catch {}
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };

    window.addEventListener("touchstart", unlock, { once: true });
    window.addEventListener("click", unlock, { once: true });

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, []);

  // ---- small helpers
  const vibrate = () => {
    if (navigator.vibrate) {
      // short pattern that feels snappy
      navigator.vibrate([20, 30, 20]);
    }
  };

    const beep = async () => {
    // Prefer WebAudio (no latency), fall back to file
    const ctx = audioCtxRef.current;
    try {
      if (ctx && ctx.state !== "suspended") {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = 880; // A5
        gain.gain.setValueAtTime(0.0001, ctx.currentTime);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        // quick attack/decay
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
        osc.stop(ctx.currentTime + 0.16);
        return;
      }
    } catch {}
    try { await fileBeepRef.current?.play(); } catch {}
  };


  useImperativeHandle(ref, () => ({
    stopScanner: async () => {
      if (html5QrcodeRef.current) {
        try { await html5QrcodeRef.current.stop(); } catch {}
        try { await html5QrcodeRef.current.clear(); } catch {}
        html5QrcodeRef.current = null;
        const container = document.getElementById(scannerId);
        if (container) container.innerHTML = "";
      }
    },
    startScanner: async () => {
      if (html5QrcodeRef.current) return;

      const html5QrCode = new Html5Qrcode(scannerId);
      html5QrcodeRef.current = html5QrCode;

      try {
        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          async (decodedText) => {
            // haptic + sound
            vibrate();
            await beep();

            onScan(decodedText);

            // stop after one scan
            try { await html5QrCode.stop(); } catch {}
            try { await html5QrCode.clear(); } catch {}
            html5QrcodeRef.current = null;
          },
          () => {}
        );
      } catch (error) {
        console.error("Failed to start scanner", error);
      }
    },
  }));

  useEffect(() => {
    if (ref && typeof ref !== "function" && ref.current) {
      ref.current.startScanner();
    }
    return () => {
      if (html5QrcodeRef.current) {
        (async () => {
          try { await html5QrcodeRef.current?.stop(); } catch {}
          try { await html5QrcodeRef.current?.clear(); } catch {}
          html5QrcodeRef.current = null;
          const container = document.getElementById(scannerId);
          if (container) container.innerHTML = "";
        })();
      }
    };
  }, [ref]);

  return <div id={scannerId} style={{ width: "100%" }} />;
});

export default QRScanner;